/**
 * Renders a module deadline as remaining time, ticking every 250ms. `running` deadlines count down from the
 * server's absolute time (adjusted by `serverTimeOffset`); `paused` deadlines show the frozen remaining time.
 */
import { useEffect, useState } from 'react';
import type { DeadlinePublic } from '@momentum/shared';

export interface DeadlineChipProps {
  deadline: DeadlinePublic;
  serverTimeOffset: number;
}

function computeRemainingMs(deadline: DeadlinePublic, serverTimeOffset: number): number {
  if (deadline.mode === 'paused') return deadline.remainingMs;
  return deadline.at - (Date.now() + serverTimeOffset);
}

function formatRemaining(ms: number): string {
  const clamped = Math.max(0, ms);
  const totalSeconds = Math.ceil(clamped / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function DeadlineChip({ deadline, serverTimeOffset }: DeadlineChipProps) {
  const [, forceTick] = useState(0);
  const runningAt = deadline.mode === 'running' ? deadline.at : null;

  useEffect(() => {
    if (deadline.mode !== 'running') return;
    const id = setInterval(() => forceTick((t) => t + 1), 250);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadline.mode, runningAt]);

  const remainingMs = computeRemainingMs(deadline, serverTimeOffset);
  const urgent = deadline.mode === 'running' && remainingMs <= 10_000;

  return (
    <span className={urgent ? 'deadline-chip deadline-chip-urgent' : 'deadline-chip'}>
      {deadline.label ? `${deadline.label}: ` : ''}
      {formatRemaining(remainingMs)}
      {deadline.mode === 'paused' ? ' (paused)' : ''}
    </span>
  );
}
